import { useCallback, useEffect, useState } from "react";
import { CloudDownload, Loader2, MessageSquareTextIcon, Sparkles } from "lucide-react";
import AdminHeader from "@/components/header/admin-header";
import useFeedbackManagerService from "@/services/feedback-manager.service";
import {
  ConversationType, FEEDBACK_SIDEBAR_WIDTH, FEEDBACK_SIDEBAR_WIDTH_CALC,
  MESSAGE_FEEDBACK, MESSAGE_FEEDBACK_POLICY
} from "@/constants/chat.constant";
import { ADMIN_PANEL_HEADER } from "@/constants/admin.constant";
import FeedbackCard from "@/components/feedback-manager/feedback-card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import PaginationLoader from "../pagination-loader";
import ConversationSidebar from "@/components/feedback-manager/feedback-sidebar";
import { ChatMessageList } from "@/interfaces/chat.interface";
import { PaginationInfo } from "@/interfaces/common.interface";
import { CitationDetailsModal } from "@/components/modal/citationDetailsModal";
import { useCitation } from "@/hooks/use-citation";
import FeedForwardAI from "./feedforward-ai";
import clsx from "clsx";
import { Separator } from "@/components/ui/separator";

const FeedbackManagerWrapper = () => {
  const FEEDBACK_PER_PAGE = 15;

  const [conversationType, setConversationType] = useState<ConversationType>(
    ConversationType.CHOP_GPT
  );
  const [feedbackList, setFeedbackList] = useState<ChatMessageList[]>([]);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] =
    useState<ChatMessageList | null>(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [showFeedForwardAI, setShowFeedForwardAI] = useState(false);

  const {
    isLoading,
    citationDetailsOpen,
    citationDetails,
    openCitationDetails,
    closeCitationDetails,
    selectedCitationId
  } = useCitation();

  const { getFeedbackMessages, exportFeedbackReport } = useFeedbackManagerService();


  useEffect(() => {
    // Fetch feedbacks when conversation type changes
    fetchFeedbacks(1);
    setPage(1);
    if (!showFeedForwardAI) {
      handleCloseSidebar();
    }

    //Cleanup: close sidebar when component is unmounted
    return () => {
      handleCloseSidebar();
    };
  }, [conversationType]);
  
  // Fetch feedbacks when FeedForward AI is opened (if list is empty)
  useEffect(() => {
    if (showFeedForwardAI && feedbackList.length === 0 && !loading) {
      fetchFeedbacks(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showFeedForwardAI]);
  
  /**
   * Fetches the list of feedback (Paginated)
   * 
   * @param pageNumber 
   * @param append 
   */
  const fetchFeedbacks = async (pageNumber: number = 1, append = false) => {
    try {
      setLoading(true);
      const res = await getFeedbackMessages(
        pageNumber,
        FEEDBACK_PER_PAGE,
        conversationType || null
      );
      setFeedbackList((prev) => (append ? [...prev, ...res.data] : res.data));
      setPagination(res.pagination);
    } catch (error) {
      console.error("Error fetching feedback messages:", error);
    } finally {
      setLoading(false);
    }
  };
  
  /**
   * Handle the toggle between different conversation types
   * 
   * @param type 
   */
  const handleToggle = (type: ConversationType) => {
    setConversationType(type);
    handleCloseSidebar();
  };

  /**
   * Load more feedback messages
   */
  const handleLoadMore = async () => {
    if (pagination?.has_next) {
      const nextPage = page + 1;
      setPage(nextPage);
      await fetchFeedbacks(nextPage, true);
    }
  };

  /**
   * Open sidebar when a particular message is clicked
   * 
   * @param message
   */
  const handleMessageClick = (message: ChatMessageList) => {
    setSelectedMessage(message);
    setIsSidebarOpen(true);
  };

  /**
   * Close sidebar when close button is clicked
   */
  const handleCloseSidebar = () => {
    setIsSidebarOpen(false);
    setSelectedMessage(null);
  };

  const handleDownloadExcel = useCallback(async () => {
    const blob = await exportFeedbackReport(conversationType);
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    const label = conversationType === "CHOP_GPT" ? "CHOP GPT Feedback" : "Policy Asst Feedback";
    link.download = `${label} ${new Date().toISOString().slice(0, 10)}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }, [conversationType, exportFeedbackReport]);

  return (
    <div className="flex flex-row flex-1 h-full w-full">
      {/*Main Panel */}
      <div
        className={clsx(
          "flex flex-col admin-background text-text-500 transition-all duration-300 p-6 px-10",
          isSidebarOpen
            ? `w-full ${FEEDBACK_SIDEBAR_WIDTH_CALC.md} ${FEEDBACK_SIDEBAR_WIDTH_CALC.lg} ${FEEDBACK_SIDEBAR_WIDTH_CALC.xl}`
            : "w-full"
        )}
      >
        <div className="flex items-center">
          <AdminHeader header={ADMIN_PANEL_HEADER.FEEDBACK_MANAGER} />
          <div className="flex items-center gap-3">
            <Separator orientation="vertical" className="h-[20px] w-[2px] bg-text-500"/>
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  handleCloseSidebar();
                  setShowFeedForwardAI(!showFeedForwardAI);
                }}
                className={clsx(
                  "flex items-center gap-2 px-4 py-1 rounded-full transition-colors text-sm font-medium",
                  showFeedForwardAI
                    ? "bg-primary-500 text-white hover:bg-primary-600"
                    : "bg-white text-primary-500 border border-primary-500 hover:bg-primary-50"
                )}
              >
                <Sparkles size={16} />
                <span>FeedForward AI</span>
              </button>
            </div>
          </div>
        </div>

        {showFeedForwardAI ? (
          <FeedForwardAI 
            onClose={() => setShowFeedForwardAI(false)} 
            conversationType={conversationType}
          />
        ) : (
          <>
            {/* Filter Tabs */}
            <div className="my-4 mb-6 flex items-center justify-between">
              <Tabs
                value={conversationType}
                onValueChange={(val) =>
                  !loading && handleToggle(val as ConversationType)
                }
              >
                <TabsList
                  className={`transition-opacity ${
                    loading ? "pointer-events-none opacity-50" : ""
                  }`}
                >
                  <TabsTrigger
                    value="CHOP_GPT"
                    className="data-[state=inactive]:text-toggleInactive data-[state=active]:bg-primary-500 data-[state=active]:text-white"
                  >
                    CHOP GPT
                  </TabsTrigger>
                  <TabsTrigger
                    value="POLICY_GPT"
                    className="data-[state=inactive]:text-toggleInactive data-[state=active]:bg-primary-500 data-[state=active]:text-white"
                  >
                    POLICY ASSISTANT
                  </TabsTrigger>
                </TabsList>
              </Tabs>

              <button
                type="button"
                onClick={handleDownloadExcel}
                className="flex items-center gap-2 text-primary-500 hover:text-primary-600 transition-colors bg-transparent border-none p-0 cursor-pointer"
                aria-label="Download as Excel"
              >
                <CloudDownload className="h-4 w-4" />
                <span className="font-primary">Download as Excel</span>
              </button>
            </div>

            {/* Feedback List */}
            <div className="flex-1 overflow-y-auto space-y-3">
              {loading && feedbackList.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-64 text-slate-600">
                  <Loader2 className="w-6 h-6 animate-spin mb-2" />
                  <p>Loading feedback messages...</p>
                </div>
              ) : feedbackList.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-64 text-slate-600">
                  <MessageSquareTextIcon className="w-8 h-8 mb-2" />
                  <p>No pending feedback found</p>
                </div>
              ) : (
                <>
                  {feedbackList.map((message) => (
                    <div
                      key={message.message_id}
                      onClick={() => handleMessageClick(message)}
                      className="cursor-pointer"
                    >
                      <FeedbackCard message={message} selected={selectedMessage?.message_id == message.message_id} />
                    </div>
                  ))}

                  {/* Load more button */}
                  <PaginationLoader
                    hasNext={!!pagination?.has_next}
                    loading={loading}
                    onLoadMore={handleLoadMore}
                  />
                </>
              )}
            </div>
          </>
        )}

        {/* Citation details*/}
        <CitationDetailsModal
          open={citationDetailsOpen}
          citation={citationDetails}
          onClose={closeCitationDetails}
          loading={isLoading}
        />
      </div>

      {/* Sidebar Panel */}
      <div
        className={clsx(
          "bg-background transition-all duration-300 overflow-y-auto h-[calc(100svh)] sticky top-0",
          isSidebarOpen
            ? `${FEEDBACK_SIDEBAR_WIDTH.base} ${FEEDBACK_SIDEBAR_WIDTH.md} ${FEEDBACK_SIDEBAR_WIDTH.lg} ${FEEDBACK_SIDEBAR_WIDTH.xl}`
            : "w-0"
        )}
      >
        {isSidebarOpen && selectedMessage && (
          <div className="h-full flex flex-col">
            <ConversationSidebar
              message={selectedMessage}
              onClose={handleCloseSidebar}
              onOpenSources={openCitationDetails}
              isCitationLoading={isLoading}
              selectedCitationId={selectedCitationId ?? null}
              messageFeedback={conversationType == ConversationType.POLICY_ASSISTANT ? MESSAGE_FEEDBACK_POLICY : MESSAGE_FEEDBACK }
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default FeedbackManagerWrapper;
